import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  verifyResetCode,
  applyPasswordReset,
  checkPasswordPolicy,
  loginAfterPasswordReset,
  completeForcedResetAfterLogin,
  logoutUser
} from "../web/src/services/authService";
import PasswordPolicyChecklist from "../web/src/services/components/PasswordPolicyChecklist";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [policyStatus, setPolicyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isResetMode = useMemo(
    () => mode === "resetPassword",
    [mode]
  );

  useEffect(() => {
    let active = true;

    async function init() {
      setLoading(true);
      setError("");

      if (!isResetMode || !oobCode) {
        setError("Geçersiz şifre sıfırlama bağlantısı.");
        setLoading(false);
        return;
      }

      try {
        const restoredEmail = await verifyResetCode(oobCode);

        if (active) {
          setEmail(restoredEmail);
        }
      } catch (err) {
        console.error("VERIFY RESET CODE ERROR:", err);

        if (active) {
          if (err?.code === "auth/invalid-action-code") {
            setError(
              "Şifre yenileme linki geçersiz veya daha önce kullanılmış."
            );
          } else if (err?.code === "auth/expired-action-code") {
            setError(
              "Şifre yenileme linkinin süresi dolmuş."
            );
          } else {
            setError(
              "Şifre sıfırlama linki geçersiz."
            );
          }
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      active = false;
    };
  }, [isResetMode, oobCode]);

  useEffect(() => {
    let active = true;

    async function validate() {
      if (!newPassword) {
        setPolicyStatus(null);
        return;
      }

      try {
        const status = await checkPasswordPolicy(newPassword);

        if (active) {
          setPolicyStatus(status);
        }
      } catch {
        if (active) {
          setPolicyStatus(null);
        }
      }
    }

    validate();

    return () => {
      active = false;
    };
  }, [newPassword]);

  async function handleSubmit(e) {
    e.preventDefault();

    setError("");
    setSuccess("");

    if (!newPassword || !confirm) {
      setError("Lütfen tüm alanları doldurun.");
      return;
    }

    if (newPassword !== confirm) {
      setError("Şifreler eşleşmiyor.");
      return;
    }

    if (policyStatus && !policyStatus.isValid) {
      setError(
        "Şifre kurallarını karşılayan bir parola girin."
      );
      return;
    }

    if (!email) {
      setError(
        "Kullanıcı e-posta adresi doğrulanamadı."
      );
      return;
    }

    try {
      setSubmitting(true);

      /*
       * 1. Firebase Auth üzerinde şifreyi değiştir.
       */
      await applyPasswordReset(
        oobCode,
        newPassword
      );

      /*
       * 2. Reset işleminin tamamlandığını aynı origin'de geçici olarak
       * kaydet.
       */
      localStorage.setItem(
        "passwordResetCompleted",
        "true"
      );

      localStorage.setItem(
        "passwordResetEmail",
        email.toLowerCase()
      );

      /*
       * 3. Yeni oluşturulan şifreyle kullanıcıyı aynı web.app origin'inde
       * otomatik olarak tekrar authenticate et.
       */
      await loginAfterPasswordReset(
        email,
        newPassword
      );

      /*
       * 4. Firestore users/{uid} dokümanındaki zorunlu şifre değiştirme
       * durumunu kapat.
       */
      await completeForcedResetAfterLogin();

      /*
       * 5. İşlem tamamlandığı için localStorage bilgilerine artık
       * ihtiyacımız yok.
       */
      localStorage.removeItem(
        "passwordResetCompleted"
      );

      localStorage.removeItem(
        "passwordResetEmail"
      );

      /*
       * 6. web.app üzerindeki geçici Auth oturumunu kapat.
       */
      await logoutUser();

      /*
       * 7. Kullanıcıyı gerçek siteye yönlendir.
       *
       * Burada kullanıcı itkstarter.org üzerinde yeni şifresiyle
       * normal giriş yapacak.
       */
      window.location.replace(
        "https://itkstarter.org"
      );

    } catch (err) {
      console.error("RESET ERROR:", err);

      /*
       * Bir hata oluşursa localStorage'da yarım kalmış reset bilgisi
       * bırakmıyoruz.
       */
      localStorage.removeItem(
        "passwordResetCompleted"
      );

      localStorage.removeItem(
        "passwordResetEmail"
      );

      if (err?.code === "auth/invalid-action-code") {
        setError(
          "Şifre yenileme linki geçersiz, daha önce kullanılmış veya bozulmuş."
        );
      } else if (
        err?.code === "auth/expired-action-code"
      ) {
        setError(
          "Şifre yenileme linkinin süresi dolmuş. Yeni link isteyin."
        );
      } else if (
        err?.code === "auth/weak-password"
      ) {
        setError(
          "Şifre yeterince güçlü değil."
        );
      } else if (
        err?.code === "auth/invalid-credential"
      ) {
        setError(
          "Şifre değiştirildi ancak oturum doğrulanamadı. Yeni şifrenizle tekrar giriş yapmayı deneyin."
        );
      } else {
        setError(
          err?.message ||
          err?.code ||
          "Şifre güncellenirken bir hata oluştu."
        );
      }

    } finally {
      setSubmitting(false);
    }
  }

  function handleGoLogin() {
    window.location.href = "https://itkstarter.org";
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          Yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <form
        style={styles.card}
        onSubmit={handleSubmit}
      >
        <h2>
          Yeni Şifre Belirle
        </h2>

        {email && (
          <p style={styles.meta}>
            Hesap:{" "}
            <strong>
              {email}
            </strong>
          </p>
        )}

        <label style={styles.label}>
          Yeni Şifre
        </label>

        <div style={styles.passwordWrapper}>
          <input
            style={styles.passwordInput}
            type={
              showPassword
                ? "text"
                : "password"
            }
            value={newPassword}
            onChange={(e) =>
              setNewPassword(e.target.value)
            }
            placeholder="Yeni şifrenizi girin"
          />

          <button
            type="button"
            onClick={() =>
              setShowPassword((prev) => !prev)
            }
            style={styles.eyeButton}
          >
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>

        <label style={styles.label}>
          Yeni Şifre Tekrar
        </label>

        <div style={styles.passwordWrapper}>
          <input
            style={styles.passwordInput}
            type={
              showConfirm
                ? "text"
                : "password"
            }
            value={confirm}
            onChange={(e) =>
              setConfirm(e.target.value)
            }
            placeholder="Şifrenizi tekrar girin"
          />

          <button
            type="button"
            onClick={() =>
              setShowConfirm((prev) => !prev)
            }
            style={styles.eyeButton}
          >
            {showConfirm ? "🙈" : "👁️"}
          </button>
        </div>

        <PasswordPolicyChecklist
          password={newPassword}
        />

        <button
          style={{
            ...styles.button,
            opacity: submitting ? 0.65 : 1,
            cursor: submitting
              ? "not-allowed"
              : "pointer"
          }}
          type="submit"
          disabled={submitting}
        >
          {submitting
            ? "Şifre güncelleniyor..."
            : "Şifreyi Güncelle"}
        </button>

        <button
          style={styles.secondaryButton}
          type="button"
          onClick={handleGoLogin}
          disabled={submitting}
        >
          Giriş Sayfasına Dön
        </button>

        {success && (
          <p style={styles.success}>
            {success}
          </p>
        )}

        {error && (
          <p style={styles.error}>
            {error}
          </p>
        )}
      </form>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f7f7f7",
    padding: "16px",
    boxSizing: "border-box"
  },

  card: {
    width: "100%",
    maxWidth: 460,
    background: "#fff",
    padding: 24,
    borderRadius: 12,
    boxShadow:
      "0 8px 24px rgba(0,0,0,0.08)",
    boxSizing: "border-box"
  },

  label: {
    display: "block",
    marginTop: 12,
    marginBottom: 6,
    fontWeight: 600
  },

  button: {
    width: "100%",
    marginTop: 18,
    padding: 12,
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },

  secondaryButton: {
    width: "100%",
    marginTop: 10,
    padding: 12,
    borderRadius: 8,
    border: "1px solid #ccc",
    background: "#fff",
    cursor: "pointer"
  },

  meta: {
    color: "#555",
    fontSize: 14,
    marginBottom: 8,
    wordBreak: "break-word"
  },

  success: {
    color: "green",
    marginTop: 12
  },

  error: {
    color: "crimson",
    marginTop: 12,
    lineHeight: 1.5
  },

  passwordWrapper: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ccc",
    borderRadius: 8,
    overflow: "hidden",
    width: "100%",
    boxSizing: "border-box"
  },

  passwordInput: {
    flex: 1,
    minWidth: 0,
    padding: 10,
    border: "none",
    outline: "none",
    boxSizing: "border-box"
  },

  eyeButton: {
    border: "none",
    background: "#fff",
    cursor: "pointer",
    padding: "0 12px",
    fontSize: 18,
    height: 40,
    flexShrink: 0
  }
};